<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  resultsCount: {
    type: Number,
    default: 0,
  },
  currentIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['search', 'next', 'previous', 'close']);

const { t } = useI18n();
const searchInput = ref(null);
const localQuery = ref(props.searchQuery);

let debounceTimer = null;

const handleInput = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit('search', localQuery.value);
  }, 300);
};

const handleKeydown = (event) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    if (event.shiftKey) {
      emit('previous');
    } else {
      emit('next');
    }
  } else if (event.key === 'Escape') {
    emit('close');
  }
};

watch(() => props.searchQuery, (newVal) => {
  localQuery.value = newVal;
});

onMounted(() => {
  nextTick(() => {
    if (searchInput.value) {
      searchInput.value.focus();
    }
  });
});
</script>

<template>
  <div class="message-search-bar flex items-center gap-2 px-4 py-2 bg-n-solid-2 border-b border-n-weak">
    <div class="flex-1 relative">
      <fluent-icon
        icon="search"
        size="16"
        class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-11"
      />
      <input
        ref="searchInput"
        v-model="localQuery"
        type="text"
        class="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand"
        :placeholder="t('CONVERSATION.SEARCH.PLACEHOLDER')"
        @input="handleInput"
        @keydown="handleKeydown"
      />
    </div>

    <div v-if="resultsCount > 0" class="flex items-center gap-1 text-sm text-n-slate-11">
      <span>{{ currentIndex + 1 }}/{{ resultsCount }}</span>
    </div>

    <div v-else-if="localQuery.length >= 2" class="text-sm text-n-slate-11">
      {{ t('CONVERSATION.SEARCH.NO_RESULTS') }}
    </div>

    <div class="flex items-center gap-1">
      <button
        class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
        :disabled="resultsCount === 0"
        :title="t('CONVERSATION.SEARCH.PREVIOUS')"
        @click="emit('previous')"
      >
        <fluent-icon icon="chevron-up" size="16" />
      </button>
      <button
        class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
        :disabled="resultsCount === 0"
        :title="t('CONVERSATION.SEARCH.NEXT')"
        @click="emit('next')"
      >
        <fluent-icon icon="chevron-down" size="16" />
      </button>
      <button
        class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
        :title="t('CONVERSATION.SEARCH.CLOSE')"
        @click="emit('close')"
      >
        <fluent-icon icon="dismiss" size="16" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.message-search-bar {
  z-index: 10;
}
</style>
